import React from "react";

import { twJoin, twMerge } from "tailwind-merge";

type CardVariant = "default" | "inverted";

type CommonProps = { className?: string };
type Props = {
  dataLoc?: string;
  variant?: CardVariant;
  children: React.ReactNode;
  fullWidth?: boolean;
  ref?: React.Ref<HTMLDivElement>;
} & Omit<React.ComponentPropsWithoutRef<"div">, "className"> &
  CommonProps;
type HeaderProps = { children: React.ReactNode } & CommonProps;
type TitleProps = { title: string; dataLoc?: string } & CommonProps;
type ContentProps = {
  children: React.ReactNode | React.ReactNode[];
} & CommonProps;

type CardComponent = React.FC<Props> & {
  Title: React.FC<TitleProps>;
  Header: React.FC<HeaderProps>;
  Content: React.FC<ContentProps>;
};

export const Card = (({
  dataLoc,
  variant = "default",
  className,
  children,
  fullWidth = false,
  ref,
  ...restOfProps
}: Props) => (
  <div
    ref={ref}
    className={twMerge(
      "rounded-md border px-4 pt-3 pb-4",
      variant === "default" && "border-transparent bg-gray-50",
      variant === "inverted" && "border-gray-200 bg-white",
      fullWidth && "w-full",
      className,
    )}
    data-loc={dataLoc}
    {...restOfProps}
  >
    {children}
  </div>
)) as CardComponent;

Card.Title = ({ className, title, dataLoc }) => (
  <span
    className={twJoin("font-inter-medium-12px text-gray-600", className)}
    data-loc={dataLoc}
  >
    {title}
  </span>
);

Card.Header = ({ className, children }) => (
  <div
    className={twJoin("flex items-center justify-between space-x-3", className)}
  >
    {children}
  </div>
);

Card.Content = ({ className, children }) => (
  <div className={twJoin("space-y-3", className)}>{children}</div>
);
